import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createContext } from "react";
import {
  IElectionRound,
  ELECTION_STATUS,
  VOTING_ROUND_STATUS,
  ApiBallot,
  ISubmitStateMap,
} from "../../domain/election";
import { Loading } from "../../components/loading";
import { IVoter } from "../../domain/voter";
import { computeInterfaceStatus, createSubmitStateMap, sortDisplayOrder } from "./util";
import { BALLOT_STATUS } from "../../domain/ballot-api";
import { ElectionContext } from "./election.context";
import { IBallotSubmission } from "../../domain/ballot-submission";
import { ApiContext } from "../../context/api.provider";

export enum INTERFACE_STATE {
  LOADING = "LOADING",
  VOTING_ALLOWED = "VOTING_ALLOWED",
  ALREADY_SUBMITTED = "ALREADY_SUBMITTED",
  ELECTION_NOT_YET_OPEN = "ELECTION_NOT_YET_OPEN",
  ELECTION_ALREADY_CLOSED = "ELECTION_ALREADY_CLOSED",
}

const allPagesSubmitted = (ballotStatus?: ISubmitStateMap) => {
  if (!ballotStatus) {
    return false;
  }
  for (const page of Object.values(ballotStatus)) {
    if (!page.self_submitted) {
      return false;
    }
  }
  return true;
}

export interface SubmitResult {
  success: boolean;
  message?: string;
  code: string;
}

interface ElectionProviderProps extends PropsWithChildren {
  voter: IVoter;
  election: IElectionRound;
}
export const ElectionProvider = ({
  children,
  election,
  voter,
}: ElectionProviderProps) => {

  const { fetch } = useContext(ApiContext);

  const { voting_payload_cache_id } = election;

  const ballots = useMemo(() => {
    return election.ballots.sort((a, b) => sortDisplayOrder(a, b));
  }, [election]);

  const [submitStateMap, setSubmitStateMap] = useState<ISubmitStateMap | undefined>(undefined);

  const fetchSubmitState = useCallback(() => {
    fetch({
      url: "/election/submit_state"
    }).then(res => {
      setSubmitStateMap(res.data);
    })
  }, [setSubmitStateMap]);

  useEffect(() => {
    if (election.status === VOTING_ROUND_STATUS.VOTING && election.election_status === ELECTION_STATUS.ACTIVE) {
      fetchSubmitState()
    } else {
      setSubmitStateMap(createSubmitStateMap(election));
    }
  }, [fetchSubmitState]);

  const interfaceState = useMemo(() => computeInterfaceStatus(election, voter, submitStateMap), [voter, election, submitStateMap]);


  const handleSubmitBallot = useCallback((ballotSubmission: IBallotSubmission) => {
    return fetch({
      url: `/ballots/${ballotSubmission.ballot_id}/submit`,
      method: "post",
      data: ballotSubmission,
    }).then((res) => {
      fetchSubmitState();
      return res;
    });
  }, [voting_payload_cache_id]);

  if (!election) {
    return <Loading source="ElectionProvider" waitingFor={{ election }} />;
  }
  if (!ballots) {
    return <Loading source="ElectionProvider" />;
  }
  if (!submitStateMap) {
    return <Loading source="submitStateMap" />
  }
  return (
    <ElectionContext.Provider
      value={{
        election,
        ballots,
        submitStateMap,
        interfaceState,
        submitBallot: handleSubmitBallot,
        voter,
      }}
    >
      {children}
    </ElectionContext.Provider>
  );
};
